<template>
  <app-tooltip
    :heading="asBody ? undefined : value"
    :body="asBody ? value : undefined"
    :placement="tooltipPosition"
    :disabled="!showTooltip"
    :break-word="breakWord"
  >
    <span
      ref="textEl"
      :class="[$attrs.class, lineClampClass]"
      >{{ lineClamp ? value : truncatedText }}</span
    >
  </app-tooltip>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';
import { truncate } from '@/utils/string';

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  maxLength: {
    type: Number,
    default: 10,
  },
  lineClamp: {
    type: Number,
    default: null,
  },
  asBody: {
    type: Boolean,
    default: false,
  },
  showTooltip: {
    type: Boolean,
    default: true,
  },
  tooltipPosition: {
    type: String,
    default: 'top',
    validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value),
  },
  breakWord: {
    type: Boolean,
    default: false,
  },
});

const textEl = ref(null);
const isOverflowingClamp = ref(false);

// Tailwind scans source text for literal class names, so dynamically built
// classes like `line-clamp-${n}` won't be picked up at build time.
const LINE_CLAMP_CLASSES = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

const lineClampClass = computed(() => LINE_CLAMP_CLASSES[props.lineClamp]);

const truncatedText = computed(() => truncate(props.value, props.maxLength));

const checkClampOverflow = () => {
  if (!props.lineClamp || !textEl.value) return;
  isOverflowingClamp.value = textEl.value.scrollHeight > textEl.value.clientHeight + 1;
};

// CSS line-clamp gives no signal on whether it actually cut anything off, so we
// measure the rendered box directly and re-check on resize to keep the tooltip
// in sync as the column width changes.
let resizeObserver;
onMounted(() => {
  if (!props.lineClamp || !textEl.value) return;
  checkClampOverflow();
  resizeObserver = new ResizeObserver(checkClampOverflow);
  resizeObserver.observe(textEl.value);
});
onBeforeUnmount(() => resizeObserver?.disconnect());
watch(
  () => props.value,
  () => nextTick(checkClampOverflow)
);

const isTruncated = computed(() =>
  props.lineClamp ? isOverflowingClamp.value : props.value?.length > props.maxLength
);
const showTooltip = computed(() => props.showTooltip && isTruncated.value);
</script>
